/**
 * This module provides a constructor function for the MnemonicsManager. This MnemonicsManager is instantiated during
 * the UIContainer's setup. It is responsible for the following:
 * 1. Showing the mnemonic characters on top of the various widgets in the Toolstrip
 * 2. Responding to various interactions like key press, clicks etc. and performing the corresponding actions
 * 3. Navigating focus among the widgets on the Toolstrip
 * 4. Executing a click on the widgets on the Toolstrip if their corresponding mnemonic characters are pressed
 *
 * There are 2 types of states that are maintained by the mnemonics manager. A mnemonicState and a focusState, each of
 * these states can have 3 possible values: null, "tab" or control. The meaning of each of these values is listed below:
 *
 * mnemonicState =
 *     null - Mnemonics are not showing
 *     "tab" - Mnemonics are showing on the tabs in the Toolstrip
 *     "control" - Mnemonics are showing for controls in the current tab in the Toolstrip
 *
 * focusState =
 *     null - No focus in Toolstrip
 *     "tab" - Focus is on the current tab in the Toolstrip
 *     "control" - Focus is on one of the controls in the current tab of the Toolstrip
 *
 * Out of the above possible 9 states (3x3), only 5 are valid and hence are represented as:
 * 1. NULL_NULL    mnemonicState === null && focusState === null
 * 2. NULL_CONTROL mnemonicState === null && focusState === "control"
 * 3. NULL_TAB     mnemonicState === null && focusState === "tab"
 * 4. CONTROL_TAB  mnemonicState === "control" && focusState === "tab"
 * 5. TAB_TAB      mnemonicState === "tab" && focusState === "tab"
 *
 */

define([
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/dom-attr",
    "dojo/dom-class",
    "dojo/has",
    "dojo/keys",
    "dojo/on",
    "dojo/query",
    "dijit/popup",
    "dijit/registry",
    "dijit/Viewport",
    "MW/layout/LayoutUtils",
    "MW/toolstrip/constants/DisplayStateConstants",
    "MW/toolstrip/constants/TypeConstants",
    "MW/uiframework/MnemonicsStates",
    "MW/uiframework/FocusManager"
], function (declare, lang, domAttr, domClass, has, keys, on, query, popup, registry, Viewport, LayoutUtils,
             DisplayStateConstants, TypeConstants, MnemonicsStates, FocusManager) {

    //Declaring the constants used for various keyCodes
    var KEYCODES = {
        A: 65,
        Z: 90,
        KEY_0: 48,
        KEY_9: 57,
        NUM_0: 96,
        NUM_9: 105
    };

    var MnemonicsManager = declare(null, {
        constructor: function (args) {

            if (!args.toolstripId) {
                throw new Error("A toolstripId needs to be provided!");
            }
            this._toolstrip = registry.byId(args.toolstripId);
            MnemonicsStates.setToolstrip(this._toolstrip);

            MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_NULL);
            this._previousKeyCodes = [];

            this._setupEventListeners();
        },

        _setupEventListeners: function () {
            this._listeners = [
                addListener(document.body, "keydown", lang.hitch(this, this._handleKeyDown), true),
                addListener(document.body, "keyup", lang.hitch(this, this._handleKeyUp), true),
                addListener(document.body, "click", lang.hitch(this, this._handleClick), true),
                addListener(document.body, "commit", lang.hitch(this, this._handleCommit), true),
                addListener(window, "blur", lang.hitch(this, this._handleBlur), false)
            ];

            // If pointer events are available use them, else use mouse events
            if (window.PointerEvent) {
                this._listeners.push(addListener(document.body, "pointerdown", lang.hitch(this, this._handleMouseDown), true));
                this._listeners.push(addListener(document.body, "pointerup", lang.hitch(this, this._handleMouseUp), true));
            } else {
                this._listeners.push(addListener(document.body, "mousedown", lang.hitch(this, this._handleMouseDown), true));
                this._listeners.push(addListener(document.body, "mouseup", lang.hitch(this, this._handleMouseUp), true));
            }
        },

        _handleCommit: function () {
            this._handleExitMnemonicsMode();
        },

        _handleBlur: function () {
            if (this._altKeyDownTimeout) {
                clearTimeout(this._altKeyDownTimeout);
                this._altKeyDownTimeout = null;
            }
            this._handleExitMnemonicsMode();
        },

        _handleClick: function (evt) {
            if (!evt.syntheticEvent) {
                if (doesNotOpenAMenu(evt.target)) {
                    this._handleExitMnemonicsMode(evt.target);
                    if (evt.target === this._toolstrip.collapseButton.domNode) {
                        FocusManager.restoreFocus(false);
                    }
                } else {
                    // AccessibilityClickMixin emits a click event on Enter/Space key press. In that case, we still
                    // want to remain in the keynav mode when a SectionPopup is opened from a SectionDropDownButton.
                    if (!findAncestor("[data-type='SectionDropDownButton']")) {
                        MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_NULL);
                    }
                }
            }
        },

        _handleMouseDown: function (evt) {
            if (!evt.syntheticEvent) {
                this._handleExitMnemonicsMode();
                this._mouseDown = true;
                if (evt.altKey) {
                    // g1683634: Set the _ignoreAltKeyUp and _ignoreAltTimeout properties to true to ensure that
                    // mnemonics are not visible during column selection
                    this._ignoreAltKeyUp = true;
                    this._ignoreAltTimeout = true;
                }
            }
        },

        _handleMouseUp: function (evt) {
            if (!evt.syntheticEvent) {
                this._mouseDown = false;
            }
        },

        _handleKeyUp: function (evt) {
            if (evt.keyCode === keys.ALT) {
                if (this._altKeyDownTimeout) {
                    clearTimeout(this._altKeyDownTimeout);
                    this._altKeyDownTimeout = null;
                }

                // TODO: Do not rely on classes for the modal dialog check instead use an API when it is available - g1724372
                // g1708154: Mnemonics should not be operable when a modal dialog is open
                if (document.body.querySelector(".mwModalDialog") || document.body.querySelector(".dijitDialog")) {
                    return;
                }

                this._handleAltKeyUp();
            }
        },

        _shouldShowMnemonics: function () {
            // g1737006: Disable Mnemonics by default, using Alt and Alt + CharacterKey on a Mac. However, there is
            // still a way to get to the mnemonics in Mac: using Ctrl + F6. So, it makes sense to run all the
            // mnemonics tests even on a Mac machine. "testForAllPlatforms" flag will ensure that Mnemonics are not
            // turned off in any platform during the tests.
            if (this.testForAllPlatforms) {
                return true;
            }

            if (this._ignoreAltTimeout) {
                return false;
            }

            return !has("mac");
        },

        _handleKeyDown: function (evt) {
            // TODO: Do not rely on classes for the modal dialog check instead use an API when it is available - g1724372
            // g1708154: Mnemonics should not be operable when a modal dialog is open
            if (document.body.querySelector(".mwModalDialog") || document.body.querySelector(".dijitDialog")) {
                return;
            }
            var keyCode = evt.keyCode;

            // If any other key is pressed while alt is held down, ignore the next alt key up
            if (evt.altKey && keyCode !== keys.ALT) {
                this._ignoreAltKeyUp = true;
                if (this._altKeyDownTimeout) {
                    clearTimeout(this._altKeyDownTimeout);
                    this._altKeyDownTimeout = null;
                }
            }

            switch (true) {
                case keyCode === keys.ALT:
                // If the "Alt Gr" key is pressed on a keyboard in another locale like German,
                // the evt.ctrlKey would be true, in this case we need to ignore the next alt keyup.
                // Also, accommodate browser changes for new behavior of AltGraph where both evt.ctrlKey and
                // evt.altKey are returned false for "Alt Gr" key. See g1903222.
                if (evt.ctrlKey || evt.key !== "Alt") {
                    this._ignoreAltKeyUp = true;
                    return;
                }

                // If the Command key is held down and then the alt key is pressed then ignore
                // the next alt key up
                if (evt.metaKey) {
                    this._ignoreAltKeyUp = true;
                    return;
                }

                evt.preventDefault();

                if (this._mouseDown) {
                    // g1683634: Set the _ignoreAltKeyUp and _ignoreAltTimeout properties to true to ensure that
                    // mnemonics should are not visible during column selection
                    this._ignoreAltKeyUp = true;
                    this._ignoreAltTimeout = true;
                }

                if (!this._altKeyDownTimeout && this._shouldShowMnemonics()) {
                    this._altKeyDownTimeout = setTimeout(lang.hitch(this, function () {
                        // TODO: Do not rely on classes for the modal dialog check instead use an API when it is available - g1724372
                        // g1708154: Mnemonics should not be operable when a modal dialog is open
                        if (document.body.querySelector(".mwModalDialog") || document.body.querySelector(".dijitDialog")) {
                            return;
                        }

                        // When Alt is held down and before mnemonics appear due to Alt timeout,
                        // if pointer down happens, we still do not want to show mnemonics
                        if (!this._ignoreAltTimeout) {
                            this._handleAltKeyDownTimeout();
                            this._ignoreAltKeyUp = true;
                        }
                    }), 1500);
                }
                return false;
                break;
            case keyCode === keys.ESCAPE:
                this._handleEscapeKey();
                this._previousKeyCodes = [];
                break;
            case keyCode === keys.ENTER:
            case keyCode === keys.SPACE:
                this._handleEnterOrSpaceKey();
                break;
            case keyCode >= KEYCODES.A && keyCode <= KEYCODES.Z:
            //numbers on top of keyboard ( 0 - 9 ) : 48 - 57
            case keyCode >= KEYCODES.KEY_0 && keyCode <= KEYCODES.KEY_9:
            //numbers on numeric keypad ( 0 - 9 ) : 96 - 105
            case keyCode >= KEYCODES.NUM_0 && keyCode <= KEYCODES.NUM_9:
                // Only react to Alt + Character key or Number key combinations if no other modifier is held down
                if (evt.altKey && !evt.ctrlKey && !evt.metaKey && this._shouldShowMnemonics()) {
                    this._ignoreAltKeyUp = true;
                    setTimeout(lang.hitch(this, function () {
                        this._ignoreAltKeyUp = false;
                    }), 1000);
                    this._handleAltPlusCharacterKey(evt);
                } else {
                    this._handleCharacterKey(evt);
                }
                break;
            }

            if (evt.altKey || MnemonicsStates.getCurrentMnemonicsState() === MnemonicsStates.NULL_NULL) {
                return true;
            }

            var isTargetInSplitButton = checkForSplitButtonCase(evt.target);
            switch (true) {
            case keyCode === keys.TAB:
                evt.preventDefault();

                if (evt.shiftKey) { // Shift + Tab is pressed
                    switch (document.activeElement) {

                        // Focus is on the toolstrip tab
                        case getSelectedTabNode(this._toolstrip):
                            this._hideMnemonics();

                            // If the toolstrip is collapsed, put it in expanded on top state before trying to focus
                            // the control in the tab content.
                            if (this._toolstrip.get("displayState") === DisplayStateConstants.COLLAPSED) {
                                this._toolstrip.set("displayState", DisplayStateConstants.EXPANDED_ON_TOP);
                            }

                            // Take the focus to the last control in the top right container
                            if (focusLastFocusableControlInTopRightContainer(this._toolstrip)) {
                                MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_TAB);
                            } else { // No items in the top right container
                                // Focus the last control in the tab content
                                if (focusLastControlInTabContent(this._toolstrip)) {
                                    MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_CONTROL);
                                }
                            }
                            break;

                        // Focus is on the first control in the tab content
                        case getFirstControlInCurrentTab(this._toolstrip):
                            // Take the focus to the currently selected tab
                            if (focusSelectedTabNode(this._toolstrip)) {
                                MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_TAB);
                            }
                            break;

                        // Focus is on the first focusable control in top right container
                        case getFirstFocusableControlInTopRightContainer(this._toolstrip):
                            // If the toolstrip is collapsed, put it in expanded on top state before trying to focus
                            // the control in the tab content.
                            if (this._toolstrip.get("displayState") === DisplayStateConstants.COLLAPSED) {
                                this._toolstrip.set("displayState", DisplayStateConstants.EXPANDED_ON_TOP);
                            }

                            // Take the focus to the last control in tab content
                            if (focusLastControlInTabContent(this._toolstrip)) {
                                MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_CONTROL);
                            }
                            break;

                        default:
                            this._handleTabNavigation("previous");
                    }
                } else { // Tab is pressed
                    switch (document.activeElement) {

                        // Focus is on the toolstrip tab
                        case getSelectedTabNode(this._toolstrip):
                            // Take the focus to the first control in its content
                            this._hideMnemonics();

                            // If the toolstrip is collapsed, put it in expanded on top state before trying to focus
                            // the control in the tab content.
                            if (this._toolstrip.get("displayState") === DisplayStateConstants.COLLAPSED) {
                                this._toolstrip.set("displayState", DisplayStateConstants.EXPANDED_ON_TOP);
                            }
                            if (focusFirstControlInCurrentTab(this._toolstrip)) {
                                MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_CONTROL);
                            }
                            break;

                        // Focus is on the last control of the tab content
                        case getLastControlInTabContent(this._toolstrip):
                            // Take the focus to the first control in the top right container
                            if (focusFirstFocusableControlInTopRightContainer(this._toolstrip)) {
                                MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_TAB);
                            } else { // No items in top right container
                                // Focus the currently selected tab
                                if (focusSelectedTabNode(this._toolstrip)) {
                                    MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_TAB);
                                }
                            }
                            break;

                        // Focus is on the last focusable control in top right container
                        case getLastFocusableControlInTopRightContainer(this._toolstrip):
                            // Take the focus to the currently selected tab
                            focusSelectedTabNode(this._toolstrip);
                            break;

                        default:
                            if (findAncestor(".mwSectionDropDownButton")) {
                                focusControlInAdjacentColumn(this._toolstrip, "next");
                            } else {
                                this._handleTabNavigation("next");
                            }

                    }
                }

                break;
            case keyCode === keys.LEFT_ARROW:
                if (["all", "horizontal"].indexOf(getMnemonicKeyNavDirection(evt.target)) > -1) {
                    if (isTargetInSplitButton && evt.target.classList.contains("mwFocusNode")) {
                        // Focus is within a SplitButton/ToggleSplitButton/QABSplitButton/QABToggleSplitButton
                        // Move focus from focusNode to buttonNode
                        // no-op, widget would handle this
                    } else {
                        this._handleHorizontalNavigation("previous");
                    }
                }
                break;
            case keyCode === keys.UP_ARROW:
                if (["all", "vertical"].indexOf(getMnemonicKeyNavDirection(evt.target)) > -1) {
                    if (isTargetInSplitButton) {
                        // Focus is within a SplitButton/ToggleSplitButton/QABSplitButton/QABToggleSplitButton

                        if (evt.target.classList.contains("mwFocusNode")) {
                            // Navigation nodes list has buttonNode and focusNode within it and in that order. If
                            // the focus is on the focusNode, up arrow would put focus on the buttonNode based on
                            // the navigation nodes list. However, visually this is not correct as the buttonNode is
                            // to the left of focusNode. So, we want to skip the buttonNode and provide focus to the
                            // previous node using "changeDelta" parameter.
                            this._handleUpKey("changeDelta");
                        }
                        if (evt.target.classList.contains("mwButtonNode")) {
                            // If the focus is on the buttonNode and up arrow is pressed, check the next control. If
                            // the next control is also a SplitButton or a ToggleSplitButton, then the focus would
                            // move to its focusNode but visually, it would make more sense to move the focus to the
                            // buttonNode. If next control is not a SplitButton or a ToggleSplitButton, we will
                            // simply focus its focusNode.
                            this._handleUpKey("checkNextControlToChangeDelta");
                        }
                    } else {
                        this._handleUpKey();
                    }
                }
                break;
            case keyCode === keys.RIGHT_ARROW:
                if (["all", "horizontal"].indexOf(getMnemonicKeyNavDirection(evt.target)) > -1) {
                    if (isTargetInSplitButton && evt.target.classList.contains("mwButtonNode")) {
                        // Focus is within a SplitButton/ToggleSplitButton/QABSplitButton/QABToggleSplitButton
                        // Move focus from buttonNode to focusNode
                        // no-op, widget would handle this
                    } else {
                        this._handleHorizontalNavigation("next");
                    }
                }
                break;
            case keyCode === keys.DOWN_ARROW:
                if (["all", "vertical"].indexOf(getMnemonicKeyNavDirection(evt.target)) > -1) {
                    if (isTargetInSplitButton) {
                        // Focus is within a SplitButton/ToggleSplitButton/QABSplitButton/QABToggleSplitButton

                        if (evt.target.classList.contains("mwButtonNode")) {
                            // Navigation nodes list has buttonNode and focusNode within it and in that order. If
                            // the focus is on the buttonNode, down arrow would put focus on the focusNode based on
                            // the navigation nodes list. However, visually this is not correct as the focusNode is
                            // to the right of buttonNode. So, we want to skip the focusNode and provide focus to the
                            // next node using "changeDelta" parameter.
                            this._handleDownKey("changeDelta");
                        }
                        if (evt.target.classList.contains("mwFocusNode")) {
                            // If the focus is on the focusNode and down arrow is pressed, check the next control. If
                            // the next control is also a SplitButton or a ToggleSplitButton, then the focus would
                            // move to its buttonNode but visually, it would make more sense to move the focus to the
                            // focusNode. If next control is not a SplitButton or a ToggleSplitButton, we will
                            // simply focus its focusNode.
                            this._handleDownKey("checkNextControlToChangeDelta");
                        }
                    } else {
                        this._handleDownKey();

                        // Check if the currently focused element has a SectionPopup ancestor and stop the event propagation
                        // to ensure that the focus border does not go off when we press down arrow key on the last item
                        // in a column within a SectionPopup. (Some code in MenuKeyNavMixin tries to focus the columnContainerWrapper
                        // node on down arrow key. Even though this node is not really focusable, it has an unfortunate
                        // consequence where CSSStateMixin gets rid of the "focused" classes which are essential to show
                        // the focus border)
                        if (findAncestor("[data-type='SectionPopup']")) {
                            evt.stopImmediatePropagation();
                        }
                    }
                }
                break;
            }
        },

        _handleAltKeyUp: function () {
            if (this._ignoreAltKeyUp || !this._shouldShowMnemonics()) {
                this._ignoreAltKeyUp = false;
                this._ignoreAltTimeout = false;
                return;
            }

            switch (MnemonicsStates.getCurrentMnemonicsState()) {
                case MnemonicsStates.NULL_NULL:

                // see g1609499
                // If any pop up is open, close it and do not enter Mnemonics mode.
                if (LayoutUtils.isPopupShowing()) {
                    popup.close();
                    if (this._toolstrip.get("displayState") === DisplayStateConstants.EXPANDED_ON_TOP) {
                        this._toolstrip.set("displayState", DisplayStateConstants.COLLAPSED);
                    }
                } else {
                    MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.TAB_TAB);

                    this._showMnemonics("tab");
                    focusCurrentTab(this._toolstrip);
                }
                break;
                case MnemonicsStates.NULL_CONTROL:
                case MnemonicsStates.CONTROL_TAB:
                case MnemonicsStates.TAB_TAB:
                    this._handleAltKeyUpForMnemonicsModeState();
                    break;
                case MnemonicsStates.NULL_TAB:
                    if (this._shouldAcceptFocus(document.activeElement)) {
                        // g1890268: For elements that accept focus, show the mnemonics again instead of exiting the
                        // mnemonics mode.
                        MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.TAB_TAB);
                        this._showMnemonics("tab");
                    } else {
                        this._handleAltKeyUpForMnemonicsModeState();
                    }
                    break;
            }
        },

        _handleAltKeyUpForMnemonicsModeState: function () {
            this._hideMnemonics();
            FocusManager.restoreFocus(true);

            MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_NULL);
            if (this._toolstrip.get("displayState") === DisplayStateConstants.EXPANDED_ON_TOP) {
                this._toolstrip.set("displayState", DisplayStateConstants.COLLAPSED);
            }
        },

        _shouldAcceptFocus: function (node) {
            while (node) {
                if (node.getAttribute && node.getAttribute("data-focus") === "accept") {
                    return true;
                }
                if (node.getAttribute && node.getAttribute("data-focus") === "refuse") {
                    return false;
                }
                node = node.parentNode;
            }
            return false;
        },

        _handleAltKeyDownTimeout: function () {
            switch (MnemonicsStates.getCurrentMnemonicsState()) {
            case MnemonicsStates.NULL_NULL:
                MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.TAB_TAB);

                this._showMnemonics("tab");
                focusCurrentTab(this._toolstrip);
                break;
            }
        },

        _handleCharacterKey: function (evt) {
            var result;

            switch (MnemonicsStates.getCurrentMnemonicsState()) {
            case MnemonicsStates.CONTROL_TAB:
                FocusManager.refuseFocus(this._toolstrip.domNode);
                result = this._executeMnemonic("control", evt);
                if (result === "fullMatch") {
                    this._hideMnemonics();
                    MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_NULL);
                }
                break;
                case MnemonicsStates.TAB_TAB:
                result = this._executeMnemonic("tab", evt);
                //if a mnemonic from QAB/Search/Logout is triggered, execute it and set the state as NULL_NULL
                if (result === "fullMatch") {
                    if (isKeyCodeInTopRightContainer(evt.keyCode, this._toolstrip.topRightContainer, this._previousKeyCodes)) {
                        this._hideMnemonics();
                        this._updateMnemonicsStateBasedOnSearchContainer(evt.keyCode);
                    } else {
                        if (this._toolstrip.get("displayState") === DisplayStateConstants.COLLAPSED) {
                            this._toolstrip.set("displayState", DisplayStateConstants.EXPANDED_ON_TOP);
                        }
                        this._hideMnemonics();
                        this._showMnemonics("control");
                        MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.CONTROL_TAB);
                    }
                } else if (result === "noMatch" && isKeyCodeInTopRightContainer(evt.keyCode, this._toolstrip.topRightContainer,
                        this._previousKeyCodes)) {
                    this._hideMnemonics();
                    MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_NULL);
                }
                break;
            }
        },

        _handleAltPlusCharacterKey: function (evt) {
            var result;

            switch (MnemonicsStates.getCurrentMnemonicsState()) {
            case MnemonicsStates.NULL_NULL:
                result = this._executeMnemonic("tab", evt);
                if (result === "fullMatch") {
                    if (isKeyCodeInTopRightContainer(evt.keyCode, this._toolstrip.topRightContainer, this._previousKeyCodes)) {
                        this._hideMnemonics();
                        this._updateMnemonicsStateBasedOnSearchContainer(evt.keyCode);
                    } else {
                        if (this._toolstrip.get("displayState") === DisplayStateConstants.COLLAPSED) {
                            this._toolstrip.set("displayState", DisplayStateConstants.EXPANDED_ON_TOP);
                        }
                        this._hideMnemonics();
                        this._showMnemonics("control");

                        setTimeout(lang.hitch(this, function () {
                            focusCurrentTab(this._toolstrip);
                        }), 0);

                        MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.CONTROL_TAB);
                    }

                } else if (result === "partialMatch") {
                    MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.TAB_TAB);
                }
                else if (result === "noMatch") {
                        if(isKeyCodeInTopRightContainer(evt.keyCode, this._toolstrip.topRightContainer,
                                this._previousKeyCodes)) {
                            this._hideMnemonics();
                            MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_NULL);
                        } else {
                            this._hideMnemonics();
                            this._showMnemonics("tab");

                            setTimeout(lang.hitch(this, function () {
                                focusCurrentTab(this._toolstrip);
                            }), 0);

                            MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.TAB_TAB);
                        }
                }

                break;
            case MnemonicsStates.CONTROL_TAB:
                result = this._executeMnemonic("control", evt);
                if (result === "fullMatch") {
                    this._hideMnemonics();
                    MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_NULL);
                }
                break;
            case MnemonicsStates.TAB_TAB:
                result = this._executeMnemonic("tab", evt);
                if (result === "fullMatch") {
                    if (isKeyCodeInTopRightContainer(evt.keyCode, this._toolstrip.topRightContainer, this._previousKeyCodes)) {
                        this._hideMnemonics();
                        this._updateMnemonicsStateBasedOnSearchContainer(evt.keyCode);
                    }
                    else {
                        if (this._toolstrip.get("displayState") === DisplayStateConstants.COLLAPSED) {
                            this._toolstrip.set("displayState", DisplayStateConstants.EXPANDED_ON_TOP);
                        }
                        this._hideMnemonics();
                        this._showMnemonics("control");
                        MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.CONTROL_TAB);
                    }
                }
                break;
            }
        },

        _handleUpKey: function (deltaAction) {
            switch (MnemonicsStates.getCurrentMnemonicsState()) {
            case MnemonicsStates.NULL_CONTROL:
                if (!focusControlInSameColumn("previous", deltaAction, this._toolstrip.collapseButton.domNode, true)) {
                    focusCurrentTab(this._toolstrip);
                    MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_TAB);
                }
                break;
            case MnemonicsStates.CONTROL_TAB:
            case MnemonicsStates.TAB_TAB:
                this._hideMnemonics();
                MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_TAB);
                break;
            }
        },

        _handleDownKey: function (deltaAction) {
            switch (MnemonicsStates.getCurrentMnemonicsState()) {
            case MnemonicsStates.NULL_CONTROL:
                focusControlInSameColumn("next", deltaAction, this._toolstrip.collapseButton.domNode);
                break;
            case MnemonicsStates.NULL_TAB:
                // If the toolstrip is collapsed, put it in expanded on top state before trying to focus the control
                // in the tab content.
                if (this._toolstrip.get("displayState") === DisplayStateConstants.COLLAPSED) {
                    this._toolstrip.set("displayState", DisplayStateConstants.EXPANDED_ON_TOP);
                }
                if (focusFirstControlInCurrentTab(this._toolstrip)) {
                    MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_CONTROL);
                }
                break;
            case MnemonicsStates.CONTROL_TAB:
            case MnemonicsStates.TAB_TAB:
                this._hideMnemonics();
                // If the toolstrip is collapsed, put it in expanded on top state before trying to focus the control
                // in the tab content.
                if (this._toolstrip.get("displayState") === DisplayStateConstants.COLLAPSED) {
                    this._toolstrip.set("displayState", DisplayStateConstants.EXPANDED_ON_TOP);
                }
                if (focusFirstControlInCurrentTab(this._toolstrip)) {
                    MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_CONTROL);
                }
                break;
            }
        },

        _handleHorizontalNavigation: function (direction) {
            switch (MnemonicsStates.getCurrentMnemonicsState()) {
            case MnemonicsStates.NULL_CONTROL:
                focusControlInAdjacentColumn(this._toolstrip, direction);
                break;
            case MnemonicsStates.NULL_TAB:
                focusAdjacentTab(this._toolstrip, direction);
                break;
            case MnemonicsStates.CONTROL_TAB:
            case MnemonicsStates.TAB_TAB:
                this._hideMnemonics();
                focusAdjacentTab(this._toolstrip, direction);
                MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_TAB);
                break;
            }
        },

        _handleTabNavigation: function (direction) {
            switch (MnemonicsStates.getCurrentMnemonicsState()) {
            case MnemonicsStates.NULL_CONTROL:
                if (!(findAncestor(".galleryPopup") || findAncestor(".galleryPanel") ||
                (findAncestor(".dijitPopup") && !findAncestor("[data-type='SectionPopup']")))) { // KeyNav should work in a SectionPopup
                    if (!focusControlInSameColumn(direction, null, this._toolstrip.collapseButton.domNode)) {
                        focusControlInAdjacentColumn(this._toolstrip, direction);
                    }
                }
                break;
            case MnemonicsStates.NULL_TAB:
                focusAdjacentTab(this._toolstrip, direction);
                break;
            case MnemonicsStates.CONTROL_TAB:
            case MnemonicsStates.TAB_TAB:
                this._hideMnemonics();
                focusAdjacentTab(this._toolstrip, direction);
                MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_TAB);
                break;
            }
        },

        _handleEnterOrSpaceKey: function () {
            switch (MnemonicsStates.getCurrentMnemonicsState()) {
                case MnemonicsStates.TAB_TAB:
                    this._hideMnemonics();
                    if (this._toolstrip.get("displayState") === DisplayStateConstants.COLLAPSED) {
                        this._toolstrip.set("displayState", DisplayStateConstants.EXPANDED_ON_TOP);
                    }
                    MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_TAB);
                    break;
                case MnemonicsStates.NULL_TAB:
                    if (this._toolstrip.get("displayState") === DisplayStateConstants.COLLAPSED) {
                        this._toolstrip.set("displayState", DisplayStateConstants.EXPANDED_ON_TOP);
                    }
                    break;
                case MnemonicsStates.NULL_CONTROL:
                    // We still want to remain in the keynav mode when a SectionPopup is opened from a SectionDropDownButton.
                    if (!findAncestor("[data-type='SectionDropDownButton']")) {
                        MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_NULL);
                    }
                    break;
            }
        },

        _handleEscapeKey: function () {
            switch (MnemonicsStates.getCurrentMnemonicsState()) {
            case MnemonicsStates.NULL_CONTROL:
                // Collapse the toolstrip only if there is no pop up open
                if (!LayoutUtils.isPopupShowing()) {
                    FocusManager.restoreFocus(true);
                    if (this._toolstrip.get("displayState") === DisplayStateConstants.EXPANDED_ON_TOP) {
                        this._toolstrip.set("displayState", DisplayStateConstants.COLLAPSED);
                    }
                }
                MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_NULL);
                break;
            case MnemonicsStates.NULL_TAB:
                if (this._toolstrip.get("displayState") === DisplayStateConstants.EXPANDED_ON_TOP) {
                    this._toolstrip.set("displayState", DisplayStateConstants.COLLAPSED);
                }
                FocusManager.restoreFocus(true);
                MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_NULL);
                break;
            case MnemonicsStates.CONTROL_TAB:
                if (this._toolstrip.get("displayState") === DisplayStateConstants.EXPANDED_ON_TOP) {
                    this._toolstrip.set("displayState", DisplayStateConstants.COLLAPSED);
                }
                this._hideMnemonics();
                this._showMnemonics("tab");
                MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.TAB_TAB);
                break;
            case MnemonicsStates.TAB_TAB:
                FocusManager.restoreFocus(true);
                this._hideMnemonics();
                MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_NULL);
                break;
            case MnemonicsStates.NULL_NULL:
                if (!LayoutUtils.isPopupShowing() && this._toolstrip.get("displayState") === DisplayStateConstants.EXPANDED_ON_TOP) {
                    this._toolstrip.set("displayState", DisplayStateConstants.COLLAPSED);
                }
                break;
            }
        },

        _handleExitMnemonicsMode: function (target) {
            switch (MnemonicsStates.getCurrentMnemonicsState()) {
            case MnemonicsStates.NULL_CONTROL:
            case MnemonicsStates.NULL_TAB:
            case MnemonicsStates.CONTROL_TAB:
                case MnemonicsStates.TAB_TAB:
                this._hideMnemonics();

                // g1777696: When the target clicked is an input field that accepts focus within toolstrip, set the
                // MnemonicsState correctly so that keyboard trap does not occur.
                if (target && (target.type === "text" || target.type === "textarea") && this._toolstrip.domNode.contains(target)) {
                    // Here, we assume that the textField/textArea widget can only be in the topRightContainer to
                    // set the NULL_TAB state because toolstrip tabs cannot have such a widget and if these widgets
                    // are not in the topRightContainer then they are within the tab contents and we set the state
                    // to NULL_CONTROL.
                    if (this._toolstrip.topRightContainer.contains(target)) {
                        MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_TAB);
                    } else {
                        MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_CONTROL);
                    }
                } else {
                    FocusManager.restoreFocus(true);
                    MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_NULL);
                }
                break;
            }

            if (this._resizeEventListener) {
                // Remove the resize event listener as we only need it when the mnemonics are on. This will also help to
                // avoid unnecessary resize event callbacks.
                this._resizeEventListener.remove();
                this._resizeEventListener = undefined;
            }
        },

        _showMnemonics: function (target, mnemonicSelector) {
            if (target === "tab") {
                addMnemonicsToTabNodes(this._toolstrip);
                addMnemonicsToQABNodes(this._toolstrip);
            }

            var selectorPrefix = target === "control" ? "[tabindex='0']" : ".mwTabButtonTextNode";
            mnemonicSelector = mnemonicSelector || "[data-mnemonic]";
            var selectorString = selectorPrefix + mnemonicSelector + ", " + selectorPrefix + " " + mnemonicSelector;

            //When target is "control", display only selected tab's mnemonics and not topRightContainer mnemonics
            var targetNodes = query(selectorString, getQueryScope(this._toolstrip, target));
            if (target == "tab") {
                targetNodes = targetNodes.concat(this._toolstrip.topRightContainer.querySelectorAll(mnemonicSelector));
            }

            if (targetNodes.length > 0) {
                this._mnemonicNodesContainer = document.createElement("div");
                targetNodes.forEach(function (targetNode) {
                    var mnemonicNode = document.createElement("div");
                    mnemonicNode.classList.add("mnemonicNode");
                    mnemonicNode.textContent = targetNode.getAttribute("data-mnemonic");
                    this._mnemonicNodesContainer.appendChild(mnemonicNode);
                    document.body.appendChild(this._mnemonicNodesContainer);
                    var targetNodeBounds = targetNode.getBoundingClientRect();
                    var mnemonicNodeBounds = mnemonicNode.getBoundingClientRect();
                    var widget = registry.getEnclosingWidget(targetNode);
                    if ((widget.get("orientation") && widget.get("orientation") === "Vertical") || domClass.contains(targetNode, "mwTabButtonTextNode")) {
                        mnemonicNode.style.left = (targetNodeBounds.left + targetNodeBounds.width / 2 - mnemonicNodeBounds.width / 2) + "px";
                    } else {
                        mnemonicNode.style.left = (targetNodeBounds.left + 12) + "px";
                    }
                    mnemonicNode.style.top = (targetNodeBounds.bottom - 5) + "px";
                }, this);
            }

            // Add an event listener for "resize" event if there isn't already
            if (!this._resizeEventListener) {
                this._resizeEventListener = on(Viewport, "resize", lang.hitch(this, function () {
                    // g1761076: Hide Mnemonics when toolstrip is resized
                    this._handleExitMnemonicsMode();
                }));
            }
        },

        _hideMnemonics: function (shouldNotClearPreviousKeyCodes) {
            if (!shouldNotClearPreviousKeyCodes) {
                this._previousKeyCodes = [];
            }
            if (this._mnemonicNodesContainer && this._mnemonicNodesContainer.parentNode) {
                this._mnemonicNodesContainer.parentNode.removeChild(this._mnemonicNodesContainer);
                this._mnemonicNodesContainer = null;
            }
        },

        _executeMnemonic: function (target, evt) {
            if (target === "tab") {
                addMnemonicsToTabNodes(this._toolstrip);
                addMnemonicsToQABNodes(this._toolstrip);
            }

            var previousKeyChars = this._previousKeyCodes.map(function (previousKeyCode) {
                return String.fromCharCode(previousKeyCode)
            }, this).join('');

            // to handle number keycodes from Numpad
            var keyCode;
            if(KEYCODES.NUM_0 <= evt.keyCode && evt.keyCode <= KEYCODES.NUM_9) {
                keyCode = evt.keyCode-KEYCODES.KEY_0;
            } else {
                keyCode = evt.keyCode;
            }
            var allKeyChars = "'" + previousKeyChars + String.fromCharCode(keyCode) + "'";

            var selectorPrefix;
            if (target === "control") {
                selectorPrefix = "[tabindex='0']";
            } else if(target === "tab" && isKeyCodeInTopRightContainer(evt.keyCode, this._toolstrip.topRightContainer,
                    this._previousKeyCodes)) {
                FocusManager.refuseFocus(this._toolstrip.domNode);
                selectorPrefix = "[tabindex='0']";
            } else {
                selectorPrefix = ".mwTabButtonTextNode";
            }

            var mnemonicSelector = "[data-mnemonic=" + allKeyChars + "]";
            var selectorString = selectorPrefix + mnemonicSelector + ", " + selectorPrefix + " " + mnemonicSelector;

            var mnemonicTarget;
            if(target === "control") {
                mnemonicTarget = getQueryScope(this._toolstrip, target).querySelector(selectorString);

            } else if(target === "tab" && isKeyCodeInTopRightContainer(evt.keyCode, this._toolstrip.topRightContainer,
                    this._previousKeyCodes)) {
                    mnemonicTarget = getMnemonicTarget(this._toolstrip.topRightContainer, selectorString);
            } else {
                mnemonicTarget = getQueryScope(this._toolstrip, target).querySelector(selectorString);
            }

            var result;

            if (mnemonicTarget) {
                if (target === "control") {
                    this._emitEvent(mnemonicTarget, "pointerdown", evt.key);
                    this._emitEvent(mnemonicTarget, "mousedown", evt.key);
                    this._emitEvent(mnemonicTarget, "pointerup", evt.key);
                    this._emitEvent(mnemonicTarget, "mouseup", evt.key);
                    mnemonicTarget.focus();
                } else if ((target === "tab") && isKeyCodeInTopRightContainer(evt.keyCode, this._toolstrip.topRightContainer,
                        this._previousKeyCodes)) {
                    this._emitEvent(mnemonicTarget, "pointerdown", evt.key);
                    this._emitEvent(mnemonicTarget, "mousedown", evt.key);
                    this._emitEvent(mnemonicTarget, "pointerup", evt.key);
                    this._emitEvent(mnemonicTarget, "mouseup", evt.key);
                    on.emit(mnemonicTarget, "input", {
                        bubbles: true,
                        cancelable: true,
                        syntheticEvent: true,
                        target: mnemonicTarget
                    });

                    mnemonicTarget.focus();
                } else {
                    addMnemonicsToTabNodes(this._toolstrip);
                }

                this._emitEvent(mnemonicTarget, "click", evt.key);

                evt.preventDefault();
                result = "fullMatch";

            } else {

                var targetNodes;
                if((target === "tab") && isKeyCodeInTopRightContainer(evt.keyCode, this._toolstrip.topRightContainer,
                        this._previousKeyCodes)) {
                    targetNodes = query(selectorPrefix + "[data-mnemonic^=" + allKeyChars + "]", getQueryScope(this._toolstrip.topRightContainer, target));
                } else {
                    targetNodes = query(selectorPrefix + "[data-mnemonic^=" + allKeyChars + "]", getQueryScope(this._toolstrip, target));
                }
                if (targetNodes.length > 0) {
                    this._hideMnemonics(true);
                    this._showMnemonics(target, "[data-mnemonic^=" + allKeyChars + "]");
                    if(KEYCODES.NUM_0 <= evt.keyCode && evt.keyCode <= KEYCODES.NUM_9) {
                        //KEYCODES.KEYCODES.KEY_0 = 48
                        this._previousKeyCodes.push(evt.keyCode-KEYCODES.KEY_0);
                    } else {
                        this._previousKeyCodes.push(evt.keyCode);
                    }
                    result = "partialMatch";
                } else {
                    result = "noMatch";
                }

            }

            return result;
        },

        /**
         * Emit an event on the target
         * @param {HTMLElement} target - mnemonicTarget
         * @param {String} type - type of event
         * @param {String} keyStroke - key pressed
         */
        _emitEvent: function (target, type, keyStroke) {
            on.emit(target, type, {
                bubbles: true,
                cancelable: true,
                syntheticEvent: true,
                target: target,
                button: 0,
                keyStroke: keyStroke
            });
        },

        /**
         * If search container is shown, check whether the keycode is same as the search container's mnemonic and set the
         * state accordingly to ensure that tab navigation works within the top right container.
         * @param {number} keyCode - character's keycode or evt.keyCode
         * @private
         */
        _updateMnemonicsStateBasedOnSearchContainer: function (keyCode) {
            if (this._toolstrip.searchContainer && !domClass.contains(this._toolstrip.searchContainer, "dijitOffScreen")) {
                var searchContainerDiv = this._toolstrip.searchContainer.querySelector("input[data-mnemonic]");
                if (searchContainerDiv && String.fromCharCode(keyCode) === searchContainerDiv.getAttribute("data-mnemonic")) {
                    MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_TAB);
                } else {
                    MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_NULL);
                }
            } else {
                MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_NULL);
            }
        },

        destroy: function () {
            this._listeners.forEach(function (listener) {
                listener.remove();
            });
            if (this._resizeEventListener) {
                this._resizeEventListener.remove();
            }
            this._hideMnemonics();
        },

        /**
         * Returns the toolstrip object.
         * @returns {Object} _toolstrip
         */
        getToolstrip: function () {
            return this._toolstrip;
        },

        /**
         * Sets the focus on the current tool strip tab.
         */
        setFocusOnCurrentToolstripTab: function () {
            MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.TAB_TAB);
            this._showMnemonics("tab");
            focusCurrentTab(this._toolstrip);
        },

        /**
         * Hides the mnemonics and removes focus from toolstrip.
         */
        removeFocusFromToolstrip: function () {
            this._hideMnemonics();
            MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.NULL_NULL);
        }
    });

    function addListener(node, eventName, callback, useCapture) {
        useCapture = !!useCapture;
        node.addEventListener(eventName, callback, useCapture);
        return {
            remove: function () {
                node.removeEventListener(eventName, callback, useCapture);
            }
        }
    }

    function findAncestor(selector) {
        var nodesMatchingSelector = Array.from(query(selector));
        var parentNode = document.activeElement.parentNode;
        while (parentNode !== null && nodesMatchingSelector.indexOf(parentNode) === -1) {
            parentNode = parentNode.parentNode;
        }
        return parentNode;
    }

    function findAllFocusableControlNodes(toolstrip) {
        var sectionPopupAncestor = findAncestor(".mwSectionPopup");
        var toolstripSelectedWidgetNode;
        if (toolstrip.tabContainer.selectedChildWidget && toolstrip.tabContainer.selectedChildWidget.domNode) {
            toolstripSelectedWidgetNode = toolstrip.tabContainer.selectedChildWidget.domNode;
        }
        var focusableNodes = query("[tabindex='0']", sectionPopupAncestor
            || toolstripSelectedWidgetNode).filter(function (node) {
            return !(node.parentNode.classList.contains("mwSectionDropDownButton") && // is in Section Drop Down (TODO: use node.matches when glee2browser is dropped)
                !(node.offsetWidth > 0 && node.offsetHeight > 0)); // not isVisible
        });

        if (!sectionPopupAncestor) {
            // Only add the toolstrip collapse expand button in the navigation cycle, if the current focus is not
            // within a SectionPopup.
            var collapseExpandButton = document.querySelector(".collapseButtonContainer [tabindex='0']");
            if (collapseExpandButton) {
                focusableNodes.push(collapseExpandButton);
            }
        }

        return focusableNodes;
    }

    function isASiblingOfActiveControl(node) {
        var result = false;

        if (document.activeElement.classList.contains("collapseButton")) {
            result = false
        } else if (findAncestor('[data-type="Gallery"]') || findAncestor('[data-type="SectionDropDownButton"]')) {
            result = false;
        } else {
            var ancestor = findAncestor('[data-type="Column"]');
            if (ancestor) {
                result = ancestor.contains(node);
            }
        }

        return result;
    }

    function findNextNodeIndex(currentNodeIndex, nodes, direction) {
        var length = nodes.length;
        var nextNodeIndex;

        if (direction === "next") {
            if (currentNodeIndex === length - 1) {
                nextNodeIndex = 0;
            } else {
                nextNodeIndex = currentNodeIndex + 1;
            }
        } else {
            if (currentNodeIndex === 0) {
                nextNodeIndex = length - 1;
            } else {
                nextNodeIndex = currentNodeIndex - 1;
            }
        }

        return nextNodeIndex;
    }

    function focusAdjacentTab(toolstrip, direction) {
        // Sometimes, the toolstrip's current mnemonic state could be other than NULL_NULL but the toolstrip would still
        // be in "refuseFocus" state. A use case for this is when we click on the search documentation textbox, we change
        // the toolstrip's mnemonic state to NULL_TAB but need the toolstrip to still remain in "refuseFocus" state to
        // ensure that any click event on the toolstrip would keep the focus on the search documentation textbox. This
        // line of code would then provide temporary focus to toolstrip enabling the tab navigation.
        MnemonicsStates.setCurrentMnemonicsState(MnemonicsStates.getCurrentMnemonicsState());

        // g1657232 temporary fix: Add mnemonics to tab and QAB nodes
        // Keyboard navigation will be separated from MnemonicsManager in the future. See g1662060 to track the progress.
        addMnemonicsToTabNodes(toolstrip);
        addMnemonicsToQABNodes(toolstrip);

        var tablist = toolstrip.tabContainer.tablist;

        // Toolstrip tabs
        var toolstripTabs = tablist.getChildren();

        // Top Right container children - all DOM Nodes
        var topRightContainerChildren = toolstrip.topRightContainer.querySelectorAll("[tabindex='0']");

        // All the elements for tab navigation
        var tabNavigationNodes = [];

        // Insert the toolstrip tabs in tabNavigationNodes
        toolstripTabs.forEach(function (toolstripTab) {
            if (toolstripTab.domNode && toolstripTab.domNode.querySelector("[data-mnemonic]")) {
                tabNavigationNodes.push(toolstripTab.domNode.querySelector("[data-mnemonic]"));
            }
        });

        // Insert the top right container elements in tabNavigationNodes
        Array.prototype.slice.call(topRightContainerChildren).forEach(function (topRightContainerChild) {
            tabNavigationNodes.push(topRightContainerChild);
        });

        // Determine the index of the currently focused element
        var focusedNode = document.activeElement;
        var focusedNodeIndex = tabNavigationNodes.indexOf(focusedNode);

        // Focus the next focusable element
        focusNextFocusableElement(tabNavigationNodes, toolstrip, tablist, toolstripTabs, focusedNodeIndex, direction);
    }

    function focusNextFocusableElement(tabNavigationNodes, toolstrip, tablist, toolstripTabs, index, direction) {
        switch (direction) {
            case "next":
                // Toolstrip tabs
                if (index < toolstripTabs.length - 1) {
                    tablist.onButtonClick(toolstrip.tabContainer.tablist.adjacent(direction === "next").page);
                }

                // Last top right container icon - focus goes to the first toolstrip tab
                else if (index === tabNavigationNodes.length - 1) {
                    tablist.onButtonClick(toolstripTabs[0].page);
                }

                // Other top right container icons except the last one
                else if (!isElementDisabled(tabNavigationNodes[index + 1])
                    && !ancestorsHasClass(tabNavigationNodes[index + 1], "dijitOffScreen")) {
                    tabNavigationNodes[index + 1].focus();
                } else {
                    focusNextFocusableElement(tabNavigationNodes, toolstrip, tablist, toolstripTabs, index + 1, direction);
                }
                break;

            case "previous":
                // Toolstrip tabs except the first one
                if (index > 0 && index < toolstripTabs.length) {
                    tablist.onButtonClick(toolstrip.tabContainer.tablist.adjacent(direction === "next").page);
                }

                // First QAB icon - focus goes to the last toolstrip tab
                else if (index === toolstripTabs.length) {
                    tablist.onButtonClick(toolstripTabs[toolstripTabs.length - 1].page);
                }

                // First toolstrip tab - focus goes to the last top right container icon
                else if (index === 0) {
                    focusNextFocusableElement(tabNavigationNodes, toolstrip, tablist, toolstripTabs, tabNavigationNodes.length,
                        direction);
                }

                // Other top right container icons except the first QAB icon
                else if (!isElementDisabled(tabNavigationNodes[index - 1])
                    && !ancestorsHasClass(tabNavigationNodes[index - 1], "dijitOffScreen")) {
                    tabNavigationNodes[index - 1].focus();
                } else {
                    focusNextFocusableElement(tabNavigationNodes, toolstrip, tablist, toolstripTabs, index - 1, direction);
                }
                break;
        }

    }

    function getMnemonicKeyNavDirection(node) {
        return domAttr.get(node, "data-mnemonic-key-nav-direction");
    }

    function focusCurrentTab(toolstrip) {
        if (toolstrip._destroyed) {
            return;
        }

        if (toolstrip.tabContainer.selectedChildWidget) {
            var tabButton = toolstrip.tabContainer.tablist.pane2button(toolstrip.tabContainer.selectedChildWidget.id);
            tabButton.domNode.querySelector(".mwTabButtonTextNode").focus();
        }
    }

    // Checks the DOM node that currently has focus. If it is within a SplitButton / ToggleSplitButton /
    // QABSplitButton / QABToggleSplitButton and the widget is horizontally oriented, returns true. Otherwise,
    // returns false.
    function checkForSplitButtonCase(target) {
        var widget;
        if (target.parentNode && target.parentNode.id) {
            widget = registry.byId(target.parentNode.id);
        }

        return widget && (widget.type === TypeConstants.SPLIT_BUTTON || widget.type === TypeConstants.TOGGLE_SPLIT_BUTTON
            || widget.type === TypeConstants.QAB_SPLIT_BUTTON || widget.type === TypeConstants.QAB_TOGGLE_SPLIT_BUTTON)
            && widget.orientation === "Horizontal";
    }

    function focusControlInSameColumn(direction, deltaAction, toolstripCollapseButton, focusSectionDropDown) {
        if (document.activeElement === toolstripCollapseButton) {
            return false;
        }

        var sectionPopup, siblings = query("[tabindex='0']", findAncestor('[data-type="Column"]'));

        if (findAncestor('[data-type="Gallery"]')) {
            siblings = [];
        }

        var currentNodeIndex = siblings.indexOf(document.activeElement);
        var delta = direction === "next" ? 1 : -1;

        if (findAncestor('.mwSectionDropDownButton')) {
            return (delta === 1); // SectionDropDownButton manages focusing of menu
        }

        if (currentNodeIndex === 0 && focusSectionDropDown && (sectionPopup = findAncestor('.toolstripPopup[dijitpopupparent] .mwSectionPopup'))) {
            var guardianFocusNode = document.querySelector('#' + sectionPopup.parentElement.getAttribute("dijitpopupparent") + ' .mwFocusNode');
            if (guardianFocusNode) {
                on.emit(sectionPopup, "restorefocus", {});
                guardianFocusNode.focus();
                return true;
            }
        }

        switch(deltaAction) {
            case "changeDelta":
                delta = direction === "next" ? 2 : -2;
                break;

            case "checkNextControlToChangeDelta":
                var nextControl = siblings[currentNodeIndex + delta];
                if (nextControl && checkForSplitButtonCase(nextControl)) {
                    delta = direction === "next" ? 2 : -2;
                }
                break;
        }

        var nextSibling = siblings[currentNodeIndex + delta];
        if (nextSibling) {
            nextSibling.focus();
            return true;
        } else {
            return false;
        }
    }

    function getFirstControlInCurrentTab(toolstrip) {
        if (findAncestor("[data-type='SectionPopup']")) {
            // Return null when the focus is within a SectionPopup as the current focused element will not be a direct
            // child of the toolstrip tab content DOM node.
            return null;
        } else {
            return findAllFocusableControlNodes(toolstrip)[0];
        }
    }

    function focusFirstControlInCurrentTab(toolstrip) {
        var firstNonDisabledWidgetFocusNode = getFirstControlInCurrentTab(toolstrip);
        if (firstNonDisabledWidgetFocusNode) {
            firstNonDisabledWidgetFocusNode.focus();
            return true;
        } else {
            return false;
        }
    }

    function getLastControlInTabContent(toolstrip) {
        if (toolstrip.get("displayState") === DisplayStateConstants.STATIC) {
            var controls = findAllFocusableControlNodes(toolstrip);
            return controls[controls.length - 1];
        } else {
            return toolstrip.collapseButton.domNode;
        }
    }

    function focusLastControlInTabContent(toolstrip) {
        var lastControlInTabContent = getLastControlInTabContent(toolstrip);
        if (lastControlInTabContent) {
            lastControlInTabContent.focus();
            return true;
        } else {
            return false;
        }
    }

    function getSelectedTabNode(toolstrip) {
        if (toolstrip.tabContainer.selectedChildWidget && toolstrip.tabContainer.selectedChildWidget.controlButton) {
            return toolstrip.tabContainer.selectedChildWidget.controlButton.focusNode;
        }

        return null;
    }

    function focusSelectedTabNode(toolstrip) {
        var selectedTabNode = getSelectedTabNode(toolstrip);
        if (selectedTabNode) {
            selectedTabNode.focus();
            return true;
        } else {
            return false;
        }
    }

    function getTopRightContainerChildren(toolstrip) {
        return Array.from(toolstrip.topRightContainer.querySelectorAll("[tabindex='0']"));
    }

    function getFirstFocusableControlInTopRightContainer(toolstrip) {
        var topRightContainerChildren = getTopRightContainerChildren(toolstrip);

        for (var i = 0; i < topRightContainerChildren.length; i++) {
            if (!ancestorsHasClass(topRightContainerChildren[i], "dijitOffScreen")) {
                return topRightContainerChildren[i];
            }
        }
    }

    function focusFirstFocusableControlInTopRightContainer(toolstrip) {
        var firstFocusableControlInTopRightContainer = getFirstFocusableControlInTopRightContainer(toolstrip);
        if (firstFocusableControlInTopRightContainer) {
            firstFocusableControlInTopRightContainer.focus();
            return true;
        } else {
            return false;
        }
    }

    function getLastFocusableControlInTopRightContainer(toolstrip) {
        var topRightContainerChildren = getTopRightContainerChildren(toolstrip);

        for (var i = topRightContainerChildren.length - 1; i >= 0; i--) {
            if (!ancestorsHasClass(topRightContainerChildren[i], "dijitOffScreen")) {
                return topRightContainerChildren[i];
            }
        }
    }

    function focusLastFocusableControlInTopRightContainer(toolstrip) {
        var lastFocusableControlInTopRightContainer = getLastFocusableControlInTopRightContainer(toolstrip);
        if (lastFocusableControlInTopRightContainer) {
            lastFocusableControlInTopRightContainer.focus();
            return true;
        } else {
            return false;
        }
    }

    function focusControlInAdjacentColumn(toolstrip, direction) {
        var nodes = findAllFocusableControlNodes(toolstrip);
        var currentNodeIndex = nodes.indexOf(document.activeElement);

        var i = findNextNodeIndex(currentNodeIndex, nodes, direction);
        while (isASiblingOfActiveControl(nodes[i]) && i !== currentNodeIndex) {
            i = findNextNodeIndex(i, nodes, direction);
        }
        nodes[i].focus();
    }

    function getQueryScope(toolstrip, target) {
        return (target === "control" && toolstrip.tabContainer.selectedChildWidget) ?
            toolstrip.tabContainer.selectedChildWidget.domNode : toolstrip.domNode;
    }

    function addMnemonicsToTabNodes(toolstrip) {
        toolstrip.tabContainer.getChildren().forEach(function (child) {
            var tabButton = toolstrip.tabContainer.tablist.pane2button(child.id);
            if (child.get("mnemonic")) {
                domAttr.set(tabButton.focusNode, "data-mnemonic", child.get("mnemonic"));
                domAttr.set(tabButton.focusNode, "data-mnemonic-key-nav-direction", "all");
            }
        }, this);
    }

    //adding sequential mnemonics to the widgets on QAB: 1-9, "01"-"09", "0A"-"0Z"
    function addMnemonicsToQABNodes(toolstrip) {
        var QABWidgets = [];
        var QAGroups = toolstrip._QABWidget.getChildren();
        var QAGroupWidgets;

        // Add widgets from all the quick access groups to the QABNodeList.
        QAGroups.forEach(function (QAGroup) {
            QAGroupWidgets = QAGroup.getChildren();
            QAGroupWidgets.forEach(function (QAGroupWidget) {
                QABWidgets.push(QAGroupWidget);
            });
        });

        QABWidgets.forEach(function (QABWidget, index) {
            QABWidget.focusNode.setAttribute("data-mnemonic", getSequentialMnemonics(index));
        });
    }

    //checking if the keycode is from topRightContainer
    function isKeyCodeInTopRightContainer(keycode, toolstrip, previousKeyCodes) {
        var mnemonicChar;

        //KEYCODES.KEY_0 = 48
        keycode = (KEYCODES.NUM_0 <= keycode && keycode <= KEYCODES.NUM_9)? keycode-KEYCODES.KEY_0 : keycode;
        var trcNodeList = toolstrip.querySelectorAll("[data-mnemonic]");

        //comparing the 'string' of keycode with 'string' of mnemonics
        for (var index = 0 ; index < trcNodeList.length; index++) {
            mnemonicChar = trcNodeList[index].getAttribute('data-mnemonic');
            if (keycode === mnemonicChar.charCodeAt(0) || (previousKeyCodes.length === 1
                && previousKeyCodes[0] === mnemonicChar.charCodeAt(0) && keycode === mnemonicChar.charCodeAt(1))) {
                return true;
            }
        }
        return false;
    }

    //obtain sequential mnemonics in the sequence: 0-9, "01"-"09", "0A"-"0Z"
    function getSequentialMnemonics(index) {
        if(index < 9){
            return (index+1).toString();
        } else if(index < 18) {
            return "0" + (index-8);
        } else if(index < 44) {
            return "0" + (String.fromCharCode(47+index)).toUpperCase();
        }
        return null;
    }

    function getMnemonicTarget(toolstrip, selectorString) {
        var mnemonicTargetTRC;
        var mnemonicTarget;
        mnemonicTargetTRC = toolstrip.querySelectorAll(selectorString);

        if(mnemonicTargetTRC.length === 1) {
            mnemonicTarget = toolstrip.querySelectorAll(selectorString)[0];
        } else if (ancestorsHasClass(mnemonicTargetTRC[0], 'dijitOffScreen')) {
            mnemonicTarget = toolstrip.querySelectorAll(selectorString)[1];
        } else {
            mnemonicTarget = toolstrip.querySelectorAll(selectorString)[0];
        }
        return mnemonicTarget;
    }

    // to identify the currently visible element, searchBox(Window size maximized) or searchButton (window size minimized)
    function ancestorsHasClass(element, className) {
        while (element) {
            if (element.classList && element.classList.contains(className)) {
                return true;
            }
            element = element.parentNode;
        }
        return false;
    }

    // Return the value of the aria-disabled attribute for the passed DOM node.
    // Returns true by default if aria-disabled attribute is not found in the DOM node or within its parents' node.
    function isElementDisabled(element) {
        while (element) {
            if (element.hasAttribute("aria-disabled")) {
                return element.getAttribute("aria-disabled") === "true";
            }
            element = element.parentNode;
        }
        return true;
    }

    function doesNotOpenAMenu(node) {
        var result = true;
        var enclosingWidget = registry.getEnclosingWidget(node);
        if (enclosingWidget) {
            result = !(enclosingWidget.domNode.classList.contains("mwMenuMixin")
                || enclosingWidget.domNode.classList.contains("mwComboBox"));
        }
        return result;
    }

    return MnemonicsManager;
});